import { useState, useRef, useEffect, useContext } from 'react';
import { Checkbox, Empty, Spin } from 'antd';
import { SearchOutlined, CloseCircleFilled } from '@ant-design/icons';
import { LenseConfig } from 'types/pricingSimulator';
import RightArrowAlt from 'assets/svg/RightArrowAlt';
import { UserContext } from 'contexts/ConfigContext';
import { getConfig, getIconByName } from 'utils/helperMethods';
import { ConfigKeys } from 'utils/Enums';
import { antIcon } from 'components/molecules/Filter/FilterApplyButton';
import _ from 'lodash';

type LensConfigMangerProps = {
    showParentIcon?: boolean;
    buttonName: string;
    initialConfigDetails: any; // config details, update the same state to see checked attributes
    onSubmitHandler: Function; // update state will be sent to the callback function
    isLoading?: boolean; // fetching config loading
    outputCallLoading?: boolean; // updating config loading
    noOutputCall?: boolean;
    cancelButtonLabel?: string;
    saveButtonLabel?: string;
    showAttributePanelToTheRight?: boolean;
    isMultiSelect?: boolean;
    placeHolderLabel?: string;
    showLensIcon?: boolean;
    reverseIconAndText?: boolean;
    disabled?: boolean;
    addAlignmentFix?: boolean; // TODO : generalize the alignment
    scrollContainerMaxHeight?: number;
    attributeHeaderName?: string;
};

const LensConfigManger = ({
    showParentIcon = true,
    buttonName,
    initialConfigDetails,
    onSubmitHandler,
    isLoading = false,
    outputCallLoading = false,
    cancelButtonLabel = 'Cancel',
    saveButtonLabel = 'Save Changes',
    showAttributePanelToTheRight = true,
    isMultiSelect = true,
    placeHolderLabel = 'Search Lenses...',
    noOutputCall = false,
    showLensIcon = false,
    reverseIconAndText = false,
    disabled = false,
    addAlignmentFix = false,
    scrollContainerMaxHeight = 300,
    attributeHeaderName = 'All Market KPIs',
}: LensConfigMangerProps) => {
    const { appConfig } = useContext(UserContext);
    const { iconsMappings } = getConfig(ConfigKeys.PRICING_SIMULATOR, appConfig);
    const [lenses, setLenses] = useState(initialConfigDetails);
    const [saveClicked, setSaveClicked] = useState(false);
    const [selectedLens, setSelectedLens] = useState<LenseConfig | null>(null);
    const [lensSelectionVisible, setLensSelectionVisible] = useState(false);
    const [attributePanelVisible, setAttributePanelVisible] = useState(false);
    const [searchKey, setSearchKey] = useState<string>('');
    const [tempLenses, setTempLenses] = useState<LenseConfig[]>([]); // To store temporary lens changes
    const selectionPanelRef = useRef<any>(null);
    const attributePanelRef = useRef<any>(null);

    const clickedLense = tempLenses.find((lens) => lens.lenseId === selectedLens?.lenseId);

    useEffect(() => {
        setLenses(initialConfigDetails);
        setSaveClicked(false);
    }, [initialConfigDetails]);

    useEffect(() => {
        if (lensSelectionVisible && lenses) {
            setTempLenses(JSON.parse(JSON.stringify(lenses)));
        }
    }, [lensSelectionVisible, initialConfigDetails]);

    // close window after
    useEffect(() => {
        if (!outputCallLoading && saveClicked) {
            setLensSelectionVisible(false);
            setAttributePanelVisible(false);
        }
    }, [outputCallLoading]);

    const handleAddLens = () => {
        if (disabled) return;
        setLensSelectionVisible(true);
    };

    const handleSelectLens = (lens: LenseConfig) => {
        setSelectedLens(lens);
        setAttributePanelVisible(true);
    };

    const handleAttributeChange = (attribute: string) => {
        let updatedLenses = _.cloneDeep(tempLenses);
        // reset all the state selection for single select mode
        if (!isMultiSelect) {
            updatedLenses = tempLenses.map((lens) => {
                return {
                    ...lens,
                    attributes: lens.attributes.map((attr) => {
                        return { ...attr, isSelected: false };
                    }),
                };
            });
        }
        updatedLenses = updatedLenses.map((lens) =>
            lens.lenseId === selectedLens?.lenseId
                ? {
                      ...lens,
                      attributes: lens.attributes.map((attr) =>
                          attr.name === attribute ? { ...attr, isSelected: !attr.isSelected } : attr,
                      ),
                  }
                : lens,
        );

        // update lense isAll selected property if all attributes are selected
        updatedLenses.forEach((item: LenseConfig) => {
            item.isAllSelected = item.attributes.every((i: any) => i.isSelected);
            item.isSelected = item.attributes.some((i: any) => i.isSelected);
        });

        setTempLenses(updatedLenses);
    };

    const handleSubmit = () => {
        setLenses(tempLenses); // Apply temporary changes to the main state
        setSaveClicked(true);
        setSelectedLens(null);
        onSubmitHandler(tempLenses);
        setSearchKey('');
        // todo : temp fix
        if (noOutputCall) {
            setLensSelectionVisible(false);
            setAttributePanelVisible(false);
        }
    };

    // both for parent and child component if clicked outside close the panels
    const handleClickOutside = (event: any) => {
        if (
            (selectionPanelRef.current &&
                !selectionPanelRef.current?.contains(event.target) &&
                attributePanelRef.current &&
                !attributePanelRef.current?.contains(event.target)) ||
            (!attributePanelRef.current &&
                selectionPanelRef.current &&
                !selectionPanelRef.current?.contains(event.target))
        ) {
            onResetClick();
        }
    };

    const onResetClick = () => {
        setLensSelectionVisible(false);
        setAttributePanelVisible(false);
        setSelectedLens(null);
        setSearchKey('');
    };

    const onSelectAllOrRemoveClick = (value: boolean) => {
        // build a Set of all attributeIds from selectedLens that need updating
        const idsToUpdate = new Set(
            selectedLens?.attributes
                .filter((attr) => attr.name.toLowerCase().includes(searchKey.toLowerCase()))
                .map((attr) => attr.attributeId),
        );

        const updatedLenses = tempLenses.map((lens) => {
            const updatedAttributes = lens.attributes.map((attr) =>
                idsToUpdate.has(attr.attributeId) ? { ...attr, isSelected: value } : attr,
            );

            // recompute flags for this lens
            const totalAttrs = updatedAttributes.length;
            const selectedCount = updatedAttributes.filter((a) => a.isSelected).length;

            return {
                ...lens,
                attributes: updatedAttributes,
                isAllSelected: selectedCount === totalAttrs && totalAttrs > 0,
                isSelected: selectedCount > 0,
            };
        });

        // update selected lens reference too
        const updatedSelectedLens = updatedLenses.find((l) => l.lenseId === selectedLens?.lenseId);

        setSelectedLens(updatedSelectedLens || null);
        setTempLenses(updatedLenses);
    };

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const isSearchResultExits =
        tempLenses?.filter(
            (l) =>
                l.lenseName?.toLocaleLowerCase().includes(searchKey.toLocaleLowerCase()) ||
                l.attributes.some((att) => att.name.toLocaleLowerCase().includes(searchKey.toLocaleLowerCase())),
        ).length > 0;

    const onSearchKeyChange = (value: string) => {
        setSearchKey(value);
        setAttributePanelVisible(false);
        setSelectedLens(null);
    };

    const isNoLensSelected = tempLenses?.every((len: LenseConfig) => !len.isSelected);

    const filteredAttributes =
        tempLenses
            .find((lens) => lens.lenseId === selectedLens?.lenseId)
            ?.attributes.filter((att) => att.name.toLocaleLowerCase().includes(searchKey.toLocaleLowerCase())) || [];

    return (
        <div className={`relative`}>
            {/* MAIN BUTTON */}
            <span
                className={`border min-w-[180px] flex justify-between items-center py-1 px-3 w-full gap-4 cursor-pointer rounded border-purpleFont text-purpleFont ${
                    isLoading ? 'pointer-events-none text-borderGreyAl1  border-borderGreyAl1' : ''
                }  ${reverseIconAndText ? 'flex-row-reverse' : ''} ${
                    disabled
                        ? 'cursor-not-allowed border-borderGrey bg-backgroundGreyAlt6  text-promoColour'
                        : 'cursor-pointer'
                }`}
                onClick={handleAddLens}
            >
                <span>{buttonName}</span>
                {showLensIcon ? getIconByName('SlidersOutlined') : getIconByName('DownOutlined')}
            </span>
            {/* SELECTION PANEL */}
            {lensSelectionVisible && (
                <div
                    className={`absolute top-9 p-3 bg-white z-50 shadow-[0px_9px_28px_8px_rgba(0,0,0,0.05)]  rounded-lg left-[${-addAlignmentFix}px]  w-[258px] flex flex-col items-start ${
                        addAlignmentFix ? 'left-[-178px]' : 'left-0'
                    }  ${outputCallLoading ? 'pointer-events-none  ' : ''} `}
                    ref={selectionPanelRef}
                >
                    {/* SEARCH  component*/}
                    <div className="  bg-backgroundGreyAlt1 p-2 w-full rounded flex gap-2 items-center mb-1">
                        <SearchOutlined style={{ color: 'gray' }} />
                        <input
                            className="border-none bg-backgroundGreyAlt1 focus:outline-none flex-grow"
                            placeholder={placeHolderLabel}
                            value={searchKey}
                            onChange={(e) => {
                                onSearchKeyChange(e.target.value);
                            }}
                        ></input>
                        {searchKey && (
                            <CloseCircleFilled
                                onClick={() => onSearchKeyChange('')}
                                style={{ color: 'gray', fontSize: '12px' }}
                            />
                        )}
                    </div>
                    {/* all attributes */}
                    <>
                        {isSearchResultExits ? (
                            <>
                                <div className="text-promoColour font-xxs mb-2">{attributeHeaderName}</div>
                                <div
                                    className={`w-full  overflow-y-auto`}
                                    style={{ maxHeight: `${scrollContainerMaxHeight}px` }}
                                >
                                    {tempLenses
                                        .filter(
                                            (l) =>
                                                l.lenseName
                                                    .toLocaleLowerCase()
                                                    .includes(searchKey.toLocaleLowerCase()) ||
                                                l.attributes.some((att) =>
                                                    att.name
                                                        .toLocaleLowerCase()
                                                        .includes(searchKey.toLocaleLowerCase()),
                                                ),
                                        )
                                        .map((lens) => {
                                            return (
                                                <div
                                                    key={lens.lenseId}
                                                    className={` rounded cursor-pointer flex items-center ${
                                                        lens.lenseId === selectedLens?.lenseId || lens.isSelected
                                                            ? 'bg-bgBackground text-purpleFont'
                                                            : ''
                                                    }`}
                                                >
                                                    <div
                                                        className="w-full flex gap-2 justify-between items-center py-2 px-3 rounded"
                                                        onClick={() => handleSelectLens(lens)}
                                                    >
                                                        <div className="w-full flex gap-2 items-center  ">
                                                            {showParentIcon &&
                                                                getIconByName(iconsMappings[lens.lenseName] || '')}
                                                            <span> {lens.lenseName}</span>
                                                        </div>

                                                        {lens.lenseId !== selectedLens?.lenseId && <RightArrowAlt />}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                </div>
                            </>
                        ) : (
                            <div className="flex items-center justify-center w-full">
                                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                            </div>
                        )}
                    </>
                    {/* BUTTONS */}
                    <div className="flex gap-2 items-center justify-end  mt-2 pt-2 w-full border-t border-backgroundGreyAlt1">
                        <span
                            className="cursor-pointer py-1 px-3 rounded border border-borderGreyAl1"
                            onClick={onResetClick}
                            style={{ minWidth: '26px' }}
                        >
                            {cancelButtonLabel}
                        </span>
                        <div
                            onClick={() => !isNoLensSelected && handleSubmit()}
                            className={` ${
                                isNoLensSelected
                                    ? 'cursor-not-allowed border-borderGrey bg-backgroundGreyAlt6  text-promoColour'
                                    : 'bg-purpleFont'
                            } px-2 font-xs cursor-pointer py-1 px-3 text-white text-center rounded `}
                            style={{ minWidth: '100px' }}
                        >
                            {outputCallLoading ? (
                                <Spin indicator={antIcon} style={{ color: 'white' }} />
                            ) : (
                                saveButtonLabel
                            )}
                        </div>
                    </div>
                </div>
            )}
            {/* ATTRIBUTES PANEL */}
            {attributePanelVisible && selectedLens && (
                <div
                    className={` absolute top-9 p-3 bg-white z-50 shadow-[0px_9px_28px_8px_rgba(0,0,0,0.05)]  rounded-lg flex flex-col w-[264px] ${
                        addAlignmentFix
                            ? 'left-[-446px]'
                            : showAttributePanelToTheRight
                            ? 'left-[262px]'
                            : 'left-[-268px]'
                    }  `}
                    ref={attributePanelRef}
                >
                    <div className="flex justify-between px-3 border-b pb-1 border-backgroundGreyAlt1 text-xs">
                        <span className="text-promoColour ">{`All ${clickedLense?.lenseName} (${
                            clickedLense?.attributes?.filter((att) =>
                                att.name.toLocaleLowerCase().includes(searchKey.toLocaleLowerCase()),
                            ).length
                        }) `}</span>

                        {isMultiSelect && (
                            <>
                                {selectedLens.isSelected &&
                                selectedLens.attributes?.filter((lens: any) => lens.isSelected)?.length > 2 ? (
                                    <div
                                        className="text-purpleFont cursor-pointer"
                                        onClick={() => onSelectAllOrRemoveClick(false)}
                                    >
                                        Unselect All
                                    </div>
                                ) : (
                                    <div
                                        className="text-purpleFont cursor-pointer"
                                        onClick={() => onSelectAllOrRemoveClick(true)}
                                    >
                                        Select All
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    <div
                        className={`w-full   overflow-y-auto `}
                        style={{ maxHeight: `${scrollContainerMaxHeight + 90}px` }}
                    >
                        {filteredAttributes.length === 0 ? (
                            <div className="text-center py-2 pt-4">No Attributes found</div>
                        ) : (
                            filteredAttributes.map((attribute, index) => {
                                return (
                                    <Checkbox
                                        checked={attribute.isSelected}
                                        onChange={() => handleAttributeChange(attribute.name)}
                                        className={` !ml-0 !py-2 !px-3 w-full ${
                                            isMultiSelect ? '' : 'checkbox-as-radio'
                                        } ${!attribute.isEditable ? 'bg-backgroundGreyAlt' : ''}`}
                                    >
                                        {attribute.name}
                                    </Checkbox>
                                );
                            })
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};
export default LensConfigManger;
